import { useParams } from "@tanstack/react-router";
import { Check, Clock, Crown, Mail, UserPlus, Users as UsersIcon, X } from "lucide-react";
import { useState } from "react";
import { AddStakeholderModal } from "@/components/members/add-stakeholder-modal";
import { InviteModal } from "@/components/members/invite-modal";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { type Invite, useInvites, useMembers, useRevokeInvite } from "@/hooks/use-members";
import { type Stakeholder, useDeleteStakeholder, useStakeholders } from "@/hooks/use-stakeholders";
import { cn } from "@/lib/utils";

const SKEL_KEYS = ["a", "b", "c"] as const;

export function MembersPage() {
  const { solutionId } = useParams({ from: "/s/$solutionId" });
  const members = useMembers(solutionId);
  const invites = useInvites(solutionId);
  const revoke = useRevokeInvite(solutionId);
  const stakeholders = useStakeholders(solutionId);
  const deleteStakeholder = useDeleteStakeholder(solutionId);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [addStakeholderOpen, setAddStakeholderOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<Invite | null>(null);
  const [deleteStakeholderTarget, setDeleteStakeholderTarget] = useState<Stakeholder | null>(null);

  const pending = invites.data?.filter((i) => i.status === "pending" && !i.isExpired) ?? [];
  const history = invites.data?.filter((i) => i.status !== "pending" || i.isExpired) ?? [];

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 space-y-6">
      <PageHeader
        title="Members"
        description="Manage who has access to this solution. Invitees get a single-use link valid for 7 days."
        actions={
          <Button onClick={() => setInviteOpen(true)}>
            <UserPlus className="h-3.5 w-3.5" />
            Invite member
          </Button>
        }
      />

      <section className="space-y-3">
        <h2 className="font-display text-sm font-semibold">
          Current members
          {members.data && (
            <span className="ml-2 text-xs text-muted-foreground font-normal">
              ({members.data.length})
            </span>
          )}
        </h2>

        {members.isLoading && (
          <ul className="space-y-2">
            {SKEL_KEYS.map((k) => (
              <Skeleton key={k} className="h-14 rounded-lg" />
            ))}
          </ul>
        )}

        {members.isError && (
          <ErrorState message="Failed to load members" onRetry={() => members.refetch()} />
        )}

        {members.data && (
          <ul className="rounded-lg border border-[var(--color-border)] bg-card divide-y divide-[var(--color-border)]">
            {members.data.map((m) => (
              <li key={m.id} className="px-4 py-3 flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{m.name ?? m.email}</div>
                  {m.name && (
                    <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                  )}
                </div>
                <RoleBadge role={m.role} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="font-display text-sm font-semibold">
          Pending invites
          {pending.length > 0 && (
            <span className="ml-2 text-xs text-muted-foreground font-normal">
              ({pending.length})
            </span>
          )}
        </h2>

        {invites.isLoading ? (
          <Skeleton className="h-14 rounded-lg" />
        ) : pending.length === 0 ? (
          <div className="text-xs text-muted-foreground italic">No pending invites.</div>
        ) : (
          <ul className="rounded-lg border border-[var(--color-border)] bg-card divide-y divide-[var(--color-border)]">
            {pending.map((inv) => (
              <li key={inv.id} className="px-4 py-3 flex items-center gap-3">
                <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{inv.email}</div>
                  <div className="text-xs text-muted-foreground">
                    Invited by {inv.invitedByName ?? inv.invitedByEmail ?? "—"} · expires{" "}
                    {new Date(inv.expiresAt).toLocaleDateString()}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setRevokeTarget(inv)}
                  className="text-destructive hover:text-destructive"
                >
                  <X className="h-3.5 w-3.5" />
                  Revoke
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {history.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-display text-sm font-semibold">
            History
            <span className="ml-2 text-xs text-muted-foreground font-normal">
              ({history.length})
            </span>
          </h2>
          <ul className="rounded-lg border border-[var(--color-border)] bg-card divide-y divide-[var(--color-border)]">
            {history.map((inv) => (
              <li key={inv.id} className="px-4 py-3 flex items-center gap-3">
                <HistoryIcon inv={inv} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{inv.email}</div>
                  <div className="text-xs text-muted-foreground">{historyLabel(inv)}</div>
                </div>
                <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                  {historyStatus(inv)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-sm font-semibold">
            Stakeholders
            {stakeholders.data && (
              <span className="ml-2 text-xs text-muted-foreground font-normal">
                ({stakeholders.data.length})
              </span>
            )}
          </h2>
          <Button size="sm" variant="outline" onClick={() => setAddStakeholderOpen(true)}>
            <UserPlus className="h-3.5 w-3.5" />
            Add stakeholder
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          People on the project who don't need a login — used as assignees on questions.
        </p>

        {stakeholders.isLoading && (
          <ul className="space-y-2">
            {SKEL_KEYS.map((k) => (
              <Skeleton key={`sk-${k}`} className="h-14 rounded-lg" />
            ))}
          </ul>
        )}

        {stakeholders.isError && (
          <ErrorState
            message="Failed to load stakeholders"
            onRetry={() => stakeholders.refetch()}
          />
        )}

        {stakeholders.data && stakeholders.data.length === 0 && (
          <div className="rounded-xl border border-dashed border-[var(--color-border)] p-8 text-center text-sm text-muted-foreground italic">
            No stakeholders yet — add the people who weren't invited but should be assignable on
            questions.
          </div>
        )}

        {stakeholders.data && stakeholders.data.length > 0 && (
          <ul className="rounded-lg border border-[var(--color-border)] bg-card divide-y divide-[var(--color-border)]">
            {stakeholders.data.map((s) => (
              <li key={s.id} className="px-4 py-3 flex items-center gap-3">
                <UsersIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{s.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {s.role} · {s.org === "client" ? "Client" : "Nymbl"}
                    {s.email && ` · ${s.email}`}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeleteStakeholderTarget(s)}
                  className="text-destructive hover:text-destructive"
                >
                  <X className="h-3.5 w-3.5" />
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <InviteModal solutionId={solutionId} open={inviteOpen} onOpenChange={setInviteOpen} />

      <AddStakeholderModal
        solutionId={solutionId}
        open={addStakeholderOpen}
        onOpenChange={setAddStakeholderOpen}
      />

      <AlertDialog open={revokeTarget !== null} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke invite?</AlertDialogTitle>
            <AlertDialogDescription>
              The link sent to <strong>{revokeTarget?.email}</strong> will stop working. They won't
              be able to use it to join the solution.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoke.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={revoke.isPending}
              onClick={async () => {
                if (!revokeTarget) return;
                await revoke.mutateAsync(revokeTarget.id);
                setRevokeTarget(null);
              }}
            >
              {revoke.isPending ? "Revoking…" : "Revoke"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleteStakeholderTarget !== null}
        onOpenChange={(o) => !o && setDeleteStakeholderTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove stakeholder?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteStakeholderTarget?.name}</strong> will be removed from this project's
              stakeholder list. Any questions currently assigned to them will become unassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteStakeholder.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteStakeholder.isPending}
              onClick={async () => {
                if (!deleteStakeholderTarget) return;
                await deleteStakeholder.mutateAsync(deleteStakeholderTarget.id);
                setDeleteStakeholderTarget(null);
              }}
            >
              {deleteStakeholder.isPending ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RoleBadge({ role }: { role: string }) {
  const isLead = role === "lead";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold",
        isLead
          ? "bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
          : "bg-muted text-muted-foreground",
      )}
    >
      {isLead && <Crown className="h-3 w-3" />}
      {role}
    </span>
  );
}

function HistoryIcon({ inv }: { inv: Invite }) {
  if (inv.status === "accepted") {
    return <Check className="h-4 w-4 text-emerald-600 shrink-0" />;
  }
  return <X className="h-4 w-4 text-muted-foreground shrink-0" />;
}

function historyStatus(inv: Invite): string {
  if (inv.status === "accepted") return "accepted";
  if (inv.status === "revoked") return "revoked";
  if (inv.isExpired) return "expired";
  return inv.status;
}

function historyLabel(inv: Invite): string {
  if (inv.status === "accepted" && inv.acceptedAt) {
    return `Accepted ${new Date(inv.acceptedAt).toLocaleDateString()}${
      inv.acceptedByEmail ? ` by ${inv.acceptedByEmail}` : ""
    }`;
  }
  if (inv.status === "revoked") return "Revoked";
  if (inv.isExpired) return `Expired ${new Date(inv.expiresAt).toLocaleDateString()}`;
  return "";
}
